import { Icon, Status, Typography } from '@statsbomb/kitbag-components';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { omit } from 'lodash';
import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { metricsAtom } from '@/atoms/metric';
import { metricGroupsAtom } from '@/atoms/queries/tableColumns';
import {
  radarTemplateActiveMetricAtom,
  radarTemplateActiveTabAtom,
  radarTemplateCustomLimitsAtom,
  radarTemplateLimitModesAtom,
} from '@/atoms/radar';
import { toastDataAtom } from '@/atoms/toast';
import { GenericToast } from '@/components/alerts/GenericToast/GenericToast';
import { CheckboxPicker } from '@/components/checkboxPicker/CheckboxPicker';
import { SortableListItem } from '@/components/listItem/SortableListItem';
import { SortableListProvider } from '@/components/listItem/SortableListProvider';
import { MAX_SELECTED_METRICS, MIN_SELECTED_METRICS } from '@/consts/radar';
import { useGroupedOptions } from '@/hooks/useGroupedOptions';
import { BaseOption } from '@/types/generic';
import { RadarTemplateModalTab } from '@/types/tabs';
import { toggleValue } from '@/utils/array';
import { mapGroupOptions, toCheckboxGroups } from '@/utils/checkboxPicker';
import { getMetricBaseName } from '@/utils/metrics';
import { isNonEmptyArray } from '@/utils/predicates';
import { camelToSnakeCase } from '@/utils/queries';
import { getTranslationMetrics } from '@/utils/translations';
import { AsideContent } from './AsideContent';
import { MainContent } from './MainContent';
import { MobileMetricSelectTabs } from './MobileMetricSelectTabs';

const translations = 'createRadarTemplateModal.step2';

type OnSelectionChange = ComponentProps<typeof CheckboxPicker>['onSelectionChange'];

const normaliseMetricValue = (option: BaseOption<string>) => ({
  ...option,
  value: camelToSnakeCase(getMetricBaseName(option.value)),
});

// extracted this component so the Suspense fallback in MainContent catches only this component
const MetricSelectMainContent = ({ onSelectionChange }: { onSelectionChange: OnSelectionChange }) => {
  const { t } = useTranslation(['modal', 'filters']);
  const selectedMetrics = useAtomValue(metricsAtom);
  const metricGroups = useAtomValue(metricGroupsAtom);
  const sortedTranslatedMetricGroups = useGroupedOptions(metricGroups);

  const groups = toCheckboxGroups(
    mapGroupOptions(sortedTranslatedMetricGroups, normaliseMetricValue),
    t('metricThreeSixtyIndicator', { ns: 'filters' }),
    t('metricPhysicalIndicator', { ns: 'filters' }),
  );

  return (
    <CheckboxPicker
      groups={groups}
      title={t(`${translations}.title`)}
      description={t(`${translations}.description`)}
      searchPlaceholder={t(`${translations}.placeholder`)}
      selectedValues={selectedMetrics}
      onSelectionChange={onSelectionChange}
    />
  );
};

export const MetricSelect = () => {
  const { t } = useTranslation(['modal', 'general', 'metrics']);
  const [selectedMetrics, setSelectedMetrics] = useAtom(metricsAtom);
  const activeTab = useAtomValue(radarTemplateActiveTabAtom);
  const [limitModes, setLimitModes] = useAtom(radarTemplateLimitModesAtom);
  const [customLimits, setCustomLimits] = useAtom(radarTemplateCustomLimitsAtom);
  const setRadarTemplateActiveMetric = useSetAtom(radarTemplateActiveMetricAtom);
  const setToastData = useSetAtom(toastDataAtom);
  const numberOfSpokes = Math.max(selectedMetrics.length, MIN_SELECTED_METRICS);

  const toggleMetric = (metric: string) => {
    if (selectedMetrics.length === MAX_SELECTED_METRICS && !selectedMetrics.includes(metric)) {
      setToastData({ toastName: 'maxMetricsReached', type: 'info' });
    } else {
      setSelectedMetrics(toggleValue(metric, selectedMetrics));
      // if the metric exists in limit modes/custom limits it means the user is doing a de-select
      // and we need to remove any custom limits associated with the metric they are deselecting
      if (limitModes[metric]) {
        setLimitModes(omit(limitModes, [metric]));
        setCustomLimits(omit(customLimits, [metric]));
        setRadarTemplateActiveMetric('');
      }
    }
  };

  const onSelectionChange: OnSelectionChange = (_event, nextMetrics) => {
    const selectedSet = new Set(selectedMetrics);
    const nextSet = new Set(nextMetrics);
    const changed =
      nextMetrics.length > selectedMetrics.length
        ? nextMetrics.filter(metric => !selectedSet.has(metric))
        : selectedMetrics.filter(metric => !nextSet.has(metric));

    if (isNonEmptyArray(changed)) toggleMetric(changed[0]);
  };

  const clearAllMetrics = () => {
    setSelectedMetrics([]);
    setLimitModes({});
    setCustomLimits({});
  };

  return (
    <>
      <MobileMetricSelectTabs />

      <MainContent className={activeTab === RadarTemplateModalTab.SELECTED_METRICS ? 'hidden md:flex' : ''}>
        <MetricSelectMainContent onSelectionChange={onSelectionChange} />
      </MainContent>
      <AsideContent
        title={t(`${translations}.aside`, { count: selectedMetrics.length })}
        onClear={clearAllMetrics}
        className={activeTab === RadarTemplateModalTab.AVAILABLE_METRICS ? 'hidden md:flex' : ''}
      >
        {selectedMetrics.length ? (
          <SortableListProvider values={selectedMetrics} setValues={setSelectedMetrics}>
            <ol className="flex flex-col gap-2 pb-4 overflow-hidden">
              {selectedMetrics.map((metric, index) => {
                const label = t(`${getTranslationMetrics(metric)}.name`, { ns: 'metrics' });

                return (
                  <SortableListItem
                    id={metric}
                    key={metric}
                    onRemove={() => toggleMetric(metric)}
                    ariaLabel={label}
                    isSortable
                    spokeIndex={index}
                    numberOfSpokes={numberOfSpokes}
                  >
                    <span className="flex-1 truncate" title={label}>
                      {label}
                    </span>
                  </SortableListItem>
                );
              })}
            </ol>
          </SortableListProvider>
        ) : (
          <Status title={t(`${translations}.status.title`)}>{t(`${translations}.status.description`)}</Status>
        )}
        {selectedMetrics.length > 0 && selectedMetrics.length < MIN_SELECTED_METRICS && (
          <Typography variant="bodyMedium" className="flex items-center gap-1 opacity-sb-secondary">
            <Icon variant="Info" size="small" />
            {t(`${translations}.feedback`, {
              metric: t(`metric.${selectedMetrics.length < MIN_SELECTED_METRICS - 1 ? 'other' : 'one'}`, {
                ns: 'general',
              }).toLowerCase(),
              number: t(`${translations}.number.${MIN_SELECTED_METRICS - 1 - selectedMetrics.length}`),
            })}
          </Typography>
        )}
      </AsideContent>

      <GenericToast />
    </>
  );
};
